import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'
import { Flag } from '../components/flag'
import { BankForm, emptyBankForm, type BankFormValue } from '../components/bankForm'

export interface Bank {
  bankId: number
  bank: string
  swift: string
  countryId: number
  country: string
  isoCode2: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface BankPage {
  banks: Bank[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * BanksPage is the bank catalogue — [Catalog].[Platform.Bank] in the BsConnekto
 * database, and the list a payout integration keys on.
 *
 * Every bank belongs to a country, and names are unique per country only:
 * "Banco Ripley" exists under both Chile and Peru, so the country column is not
 * decoration — it is what tells two identically named rows apart.
 */
export function BanksPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { countries } = useReference()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Bank | null>(null)
  const [form, setForm] = useState<BankFormValue>(emptyBankForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(countryFilter ? { countryId: countryFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['banks', params],
    queryFn: async (): Promise<BankPage> => {
      const res = await api.get('/banks', { params })
      return res.data as BankPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['banks'] })

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyBankForm, countryId: countryFilter })
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (b: Bank) => {
    setEditing(b)
    setForm({ bank: b.bank, swift: b.swift, countryId: String(b.countryId) })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        bank: form.bank.trim(),
        swift: form.swift.trim(),
        countryId: Number(form.countryId),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing ? api.put(`/banks/${editing.bankId}`, body) : api.post('/banks', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (b: Bank) =>
      b.isDisabledByPlatform
        ? api.post(`/banks/${b.bankId}/restore`)
        : api.delete(`/banks/${b.bankId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('banks.errors.save'))),
  })

  const banks = data?.banks ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('banks.title')}
        subtitle={t('banks.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('banks.newBank')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? t('banks.editBank') : t('banks.newBank')}</DialogTitle>
                </DialogHeader>
                <BankForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  countries={countries}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="banks">
        <p>{t('banks.help.line1')}</p>
        <p>{t('banks.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('banks.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || countryFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setCountryFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={countryFilter}
          onValueChange={(v) => {
            setCountryFilter(v)
            setOffset(0)
          }}
          items={countries.map((c) => ({ value: String(c.id), label: `${c.isoCode2} — ${c.name}` }))}
          placeholder={t('banks.allCountries')}
          className="w-56"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('banks.columns.id')}</TableHead>
                  <TableHead>{t('banks.columns.name')}</TableHead>
                  <TableHead className="w-32">{t('banks.columns.swift')}</TableHead>
                  <TableHead className="w-64">{t('banks.columns.country')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {banks.map((b) => (
                  <TableRow key={b.bankId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {b.bankId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/banks/${b.bankId}`} className="hover:underline">
                        {b.bank}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{b.swift || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {b.country ? (
                        <span className="flex items-center gap-2">
                          <Flag region={b.isoCode2} />
                          <span className="font-mono text-xs">{b.isoCode2}</span>
                          {b.country}
                        </span>
                      ) : (
                        `#${b.countryId}`
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={b.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(b)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={b.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(b)}
                          >
                            {b.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {banks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 6 : 5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('banks.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('banks.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
